import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '16px',
  },
  svgContainer: {
    width: '100%',
    overflowX: 'auto',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#6b7280',
    fontSize: '14px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    backgroundColor: 'rgba(17, 24, 39, 0.95)',
    color: '#f9fafb',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: '1.5',
    zIndex: 9999,
    maxWidth: '240px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
  },
  tooltipLabel: {
    fontWeight: 600,
    color: '#d1d5db',
    marginRight: '4px',
  },
  legendContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '16px',
    gap: '8px',
    fontSize: '12px',
    color: '#6b7280',
  },
};

export default function PriceElasticityHeatmap({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const fieldPairs = useMemo(() => {
    if (!data || !data.fields) return [];
    const pairs = [];
    for (let i = 0; i < data.fields.length; i++) {
      for (let j = 0; j < data.fields.length; j++) {
        if (i !== j) {
          pairs.push({ field1: data.fields[i], field2: data.fields[j] });
        }
      }
    }
    return pairs;
  }, [data]);

  const cellMap = useMemo(() => {
    if (!data || !data.cells) return new Map();
    const map = new Map();
    data.cells.forEach((cell) => {
      const key = `${cell.state}|${cell.field1}|${cell.field2}`;
      map.set(key, cell);
    });
    return map;
  }, [data]);

  useEffect(() => {
    if (!data || !data.fields || !data.states || !data.cells || !svgRef.current) return;
    if (data.states.length === 0 || fieldPairs.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const labels = data.fieldLabels || {};
    const getLabel = (f) => labels[f] || f;

    const margin = { top: 80, right: 30, bottom: 20, left: 60 };
    const cellSize = 48;
    const cellPad = 2;
    const width = margin.left + fieldPairs.length * (cellSize + cellPad) + margin.right;
    const height = margin.top + data.states.length * (cellSize + cellPad) + margin.bottom;

    svg.attr('width', width).attr('height', height);

    const allElasticities = data.cells.map((c) => c.elasticity);
    const maxAbs = d3.max(allElasticities, (v) => Math.abs(v)) || 1;
    const domainBound = Math.max(maxAbs, 0.1);

    const colorScale = d3
      .scaleLinear()
      .domain([-domainBound, 0, domainBound])
      .range(['#2563eb', '#ffffff', '#dc2626'])
      .clamp(true);

    const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Column headers (field pairs)
    fieldPairs.forEach((pair, i) => {
      const x = i * (cellSize + cellPad) + cellSize / 2;
      const label = `${getLabel(pair.field1)} \u2192 ${getLabel(pair.field2)}`;
      g.append('text')
        .attr('x', x)
        .attr('y', -10)
        .attr('text-anchor', 'end')
        .attr('transform', `rotate(-45, ${x}, -10)`)
        .style('font-size', '10px')
        .style('fill', '#4b5563')
        .style('font-family', styles.card.fontFamily)
        .text(label);
    });

    // Row headers (states)
    data.states.forEach((state, j) => {
      const y = j * (cellSize + cellPad) + cellSize / 2;
      g.append('text')
        .attr('x', -8)
        .attr('y', y)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .style('font-size', '11px')
        .style('font-weight', 500)
        .style('fill', '#374151')
        .style('font-family', styles.card.fontFamily)
        .text(state);
    });

    // Cells
    data.states.forEach((state, j) => {
      fieldPairs.forEach((pair, i) => {
        const key = `${state}|${pair.field1}|${pair.field2}`;
        const cell = cellMap.get(key);

        const x = i * (cellSize + cellPad);
        const y = j * (cellSize + cellPad);

        const cellGroup = g.append('g');

        const elasticity = cell ? cell.elasticity : null;
        const bgColor = elasticity !== null ? colorScale(elasticity) : '#f3f4f6';

        // Determine text color for readability
        const absVal = elasticity !== null ? Math.abs(elasticity) : 0;
        const textColor = absVal > domainBound * 0.6 ? '#ffffff' : '#1f2937';

        cellGroup
          .append('rect')
          .attr('x', x)
          .attr('y', y)
          .attr('width', cellSize)
          .attr('height', cellSize)
          .attr('rx', 3)
          .attr('fill', bgColor)
          .attr('stroke', '#e5e7eb')
          .attr('stroke-width', 0.5)
          .style('cursor', 'pointer')
          .on('mouseenter', (event) => {
            d3.select(event.target).attr('stroke', '#1f2937').attr('stroke-width', 2);
            setTooltip({
              x: event.clientX,
              y: event.clientY,
              state,
              field1: getLabel(pair.field1),
              field2: getLabel(pair.field2),
              elasticity: elasticity !== null ? elasticity.toFixed(3) : 'N/A',
              sampleSize: cell ? cell.sampleSize : 0,
            });
          })
          .on('mousemove', (event) => {
            setTooltip((prev) =>
              prev ? { ...prev, x: event.clientX, y: event.clientY } : null
            );
          })
          .on('mouseleave', (event) => {
            d3.select(event.target).attr('stroke', '#e5e7eb').attr('stroke-width', 0.5);
            setTooltip(null);
          });

        if (elasticity !== null) {
          cellGroup
            .append('text')
            .attr('x', x + cellSize / 2)
            .attr('y', y + cellSize / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .style('font-size', '10px')
            .style('font-weight', 500)
            .style('fill', textColor)
            .style('pointer-events', 'none')
            .style('font-family', styles.card.fontFamily)
            .text(elasticity.toFixed(2));
        }
      });
    });
  }, [data, fieldPairs, cellMap]);

  if (!data || !data.fields || !data.states || !data.cells) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>Cross-Field Price Elasticity Heatmap</div>
        <div style={styles.emptyState}>No elasticity data available.</div>
      </div>
    );
  }

  // Build legend gradient data
  const allElasticities = data.cells.map((c) => c.elasticity);
  const maxAbs = d3.max(allElasticities, (v) => Math.abs(v)) || 1;
  const domainBound = Math.max(maxAbs, 0.1);

  const legendWidth = 240;
  const legendSteps = 50;
  const legendScale = d3
    .scaleLinear()
    .domain([-domainBound, 0, domainBound])
    .range(['#2563eb', '#ffffff', '#dc2626'])
    .clamp(true);

  const legendColors = Array.from({ length: legendSteps }, (_, i) => {
    const t = -domainBound + (2 * domainBound * i) / (legendSteps - 1);
    return { offset: i, value: t, color: legendScale(t) };
  });

  const cellSize = 48;
  const cellPad = 2;
  const svgWidth =
    60 + (data.fields.length * (data.fields.length - 1)) * (cellSize + cellPad) + 30;
  const svgHeight = 80 + data.states.length * (cellSize + cellPad) + 20;

  return (
    <div style={styles.card}>
      <div style={styles.title}>Cross-Field Price Elasticity Heatmap</div>
      <div style={styles.svgContainer}>
        <svg ref={svgRef} width={svgWidth} height={svgHeight} />
      </div>

      {/* Color legend */}
      <div style={styles.legendContainer}>
        <span>-{domainBound.toFixed(2)}</span>
        <svg width={legendWidth} height={14}>
          {legendColors.map((lc, i) => (
            <rect
              key={i}
              x={(legendWidth / legendSteps) * i}
              y={0}
              width={legendWidth / legendSteps + 1}
              height={14}
              fill={lc.color}
            />
          ))}
          <rect
            x={0}
            y={0}
            width={legendWidth}
            height={14}
            fill="none"
            stroke="#d1d5db"
            strokeWidth={1}
            rx={2}
          />
        </svg>
        <span>+{domainBound.toFixed(2)}</span>
      </div>
      <div
        style={{
          textAlign: 'center',
          fontSize: '11px',
          color: '#9ca3af',
          marginTop: '4px',
        }}
      >
        Negative (blue) = inverse relationship &middot; Positive (red) = direct relationship
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x + 12,
            top: tooltip.y - 10,
          }}
        >
          <div>
            <span style={styles.tooltipLabel}>State:</span>
            {tooltip.state}
          </div>
          <div>
            <span style={styles.tooltipLabel}>Pair:</span>
            {tooltip.field1} &rarr; {tooltip.field2}
          </div>
          <div>
            <span style={styles.tooltipLabel}>Elasticity:</span>
            {tooltip.elasticity}
          </div>
          <div>
            <span style={styles.tooltipLabel}>Sample Size:</span>
            {tooltip.sampleSize}
          </div>
        </div>
      )}
    </div>
  );
}
